import { useState, type FormEvent } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/TextField'
import { updateOwnEmail, updateOwnPassword } from '@/data/settingsApi'
import { errorMessage } from '@/lib/errors'

type Banner = { type: 'success' | 'error'; text: string } | null

function EmailForm({ current, onDone }: { current: string; onDone: (text: string) => void }) {
  const [email, setEmail] = useState(current)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!email.trim()) {
      setError('Enter an email address.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await updateOwnEmail(email.trim())
      onDone('Email updated.')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      <TextField
        id="settings-email"
        label="Admin email"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        autoComplete="email"
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <Button type="submit" disabled={submitting}>
        {submitting ? 'Saving…' : 'Update email'}
      </Button>
    </form>
  )
}

function PasswordForm({ onDone }: { onDone: (text: string) => void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      await updateOwnPassword(password)
      setPassword('')
      onDone('Password updated.')
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="space-y-4" onSubmit={handleSubmit} noValidate>
      <TextField
        id="settings-password"
        label="New password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="new-password"
      />
      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}
      <Button type="submit" disabled={submitting}>
        {submitting ? 'Saving…' : 'Update password'}
      </Button>
    </form>
  )
}

export function SettingsPage() {
  const { user } = useAuth()
  const [banner, setBanner] = useState<Banner>(null)
  const done = (text: string) => setBanner({ type: 'success', text })

  return (
    <div className="max-w-md">
      <h1 className="mb-6 text-2xl font-semibold text-slate-800">Settings</h1>

      {banner && (
        <div
          className={`mb-4 rounded-lg px-4 py-2 text-sm ${
            banner.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          {banner.text}
        </div>
      )}

      <section className="mb-8">
        <h2 className="mb-2 text-lg font-semibold text-slate-700">Email</h2>
        <EmailForm current={user?.email ?? ''} onDone={done} />
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold text-slate-700">Password</h2>
        <PasswordForm onDone={done} />
      </section>
    </div>
  )
}
